import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { toast } from "sonner";
import { ListTree, Plus, Pencil, Trash2, Check, X } from "lucide-react";

const inputCls = "w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light";
const COULEURS = ["#282194", "#409AC3", "#67BEDE", "#1E8E5A", "#B7791F", "#C0392B", "#7EE8D8", "#5C5763"];

function ColorPicker({ value, onChange, testid }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={testid}>
      {COULEURS.map((c) => (
        <button key={c} type="button" data-testid={`${testid}-${c.replace("#", "")}`}
          onClick={() => onChange(c)}
          className={`h-6 w-6 rounded-full border-2 transition-all ${value === c ? "border-cfhtb-heading scale-110" : "border-transparent hover:scale-105"}`}
          style={{ backgroundColor: c }} aria-label={c} />
      ))}
    </div>
  );
}

// Simple label-only CRUD (instituts, professions)
function CrudCard({ titre, description, items, testPrefix, onAdd, onRename, onDelete }) {
  const [nouveau, setNouveau] = useState("");
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [busy, setBusy] = useState(false);
  const wrap = (fn) => async (...a) => { setBusy(true); try { await fn(...a); } finally { setBusy(false); } };
  const add = wrap(async () => { if (!nouveau.trim()) return; await onAdd(nouveau.trim()); setNouveau(""); });
  const rename = wrap(async (id) => { if (!editVal.trim()) return; await onRename(id, editVal.trim()); setEditId(null); });

  return (
    <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm" data-testid={`${testPrefix}-card`}>
      <div className="px-5 py-3 border-b border-slate-100 bg-cfhtb-bg/40">
        <h2 className="font-serif text-lg font-semibold text-cfhtb-heading">{titre}</h2>
        <p className="text-xs text-cfhtb-text mt-0.5">{description}</p>
      </div>
      <div className="p-4 flex gap-2 border-b border-slate-100">
        <input data-testid={`${testPrefix}-add-input`} className={inputCls} value={nouveau}
          onChange={(e) => setNouveau(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder="Nouvel élément…" />
        <button data-testid={`${testPrefix}-add-button`} onClick={add} disabled={busy || !nouveau.trim()}
          className="inline-flex items-center gap-1.5 bg-cfhtb-primary text-white rounded-md px-3 py-2 text-sm font-medium hover:bg-[#1f1975] transition-colors disabled:opacity-50 shrink-0">
          <Plus className="h-4 w-4" /> Ajouter
        </button>
      </div>
      <ul className="divide-y divide-slate-50 max-h-80 overflow-y-auto" data-testid={`${testPrefix}-list`}>
        {items.length === 0 && <li className="px-5 py-6 text-sm text-cfhtb-text text-center">Aucun élément.</li>}
        {items.map((it) => (
          <li key={it.id} className="px-5 py-2.5 flex items-center justify-between gap-3" data-testid={`${testPrefix}-row-${it.id}`}>
            {editId === it.id ? (
              <div className="flex items-center gap-2 flex-1">
                <input data-testid={`${testPrefix}-edit-input`} className={inputCls} value={editVal}
                  onChange={(e) => setEditVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && rename(it.id)} autoFocus />
                <button onClick={() => rename(it.id)} data-testid={`${testPrefix}-confirm-${it.id}`} className="p-1.5 rounded-md text-cfhtb-success hover:bg-cfhtb-success/10"><Check className="h-4 w-4" /></button>
                <button onClick={() => setEditId(null)} className="p-1.5 rounded-md text-cfhtb-text hover:bg-slate-100"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <>
                <span className="text-sm text-cfhtb-heading">{it.label}</span>
                <div className="flex items-center gap-1 shrink-0">
                  <button data-testid={`${testPrefix}-edit-${it.id}`} onClick={() => { setEditId(it.id); setEditVal(it.label); }} className="p-1.5 rounded-md text-cfhtb-text hover:bg-cfhtb-bg hover:text-cfhtb-primary"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`${testPrefix}-delete-${it.id}`} onClick={() => onDelete(it)} className="p-1.5 rounded-md text-cfhtb-text hover:bg-cfhtb-error/10 hover:text-cfhtb-error"><Trash2 className="h-4 w-4" /></button>
                </div>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Tags (nom + couleur)
function TagsCard() {
  const [tags, setTags] = useState(null);
  const [nom, setNom] = useState("");
  const [couleur, setCouleur] = useState(COULEURS[1]);
  const [editing, setEditing] = useState(null);
  const load = () => api("/admin/tags").then(setTags).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!nom.trim()) return;
    try { await api("/admin/tags", { method: "POST", body: { nom, couleur } }); toast.success("Tag créé"); setNom(""); load(); }
    catch (e) { toast.error(e.message); }
  };
  const save = async () => {
    try { await api(`/admin/tags/${editing.id}`, { method: "PUT", body: { nom: editing.nom, couleur: editing.couleur } }); toast.success("Tag mis à jour"); setEditing(null); load(); }
    catch (e) { toast.error(e.message); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Supprimer le tag « ${t.nom} » ? Il sera retiré des ${t.nb_membres} membre(s).`)) return;
    try { await api(`/admin/tags/${t.id}`, { method: "DELETE" }); toast.success("Tag supprimé"); load(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm" data-testid="ref-tags-card">
      <div className="px-5 py-3 border-b border-slate-100 bg-cfhtb-bg/40">
        <h2 className="font-serif text-lg font-semibold text-cfhtb-heading">Tags</h2>
        <p className="text-xs text-cfhtb-text mt-0.5">Étiquettes libres affectées aux membres, utilisables comme critère de campagne.</p>
      </div>
      <div className="p-4 space-y-2 border-b border-slate-100">
        <div className="flex gap-2">
          <input data-testid="ref-tag-add-input" className={inputCls} value={nom} onChange={(e) => setNom(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), create())} placeholder="Ex. Formateur, Bureau…" />
          <button data-testid="ref-tag-add-button" onClick={create} disabled={!nom.trim()}
            className="inline-flex items-center gap-1.5 bg-cfhtb-primary text-white rounded-md px-3 py-2 text-sm font-medium hover:bg-[#1f1975] disabled:opacity-50 shrink-0"><Plus className="h-4 w-4" /> Ajouter</button>
        </div>
        <ColorPicker value={couleur} onChange={setCouleur} testid="ref-tag-color" />
      </div>
      <ul className="divide-y divide-slate-50 max-h-80 overflow-y-auto" data-testid="ref-tags-list">
        {!tags ? <li className="px-5 py-6 text-sm text-cfhtb-text text-center">Chargement…</li>
          : tags.length === 0 ? <li className="px-5 py-6 text-sm text-cfhtb-text text-center">Aucun tag.</li>
          : tags.map((t) => (
            <li key={t.id} className="px-5 py-2.5 flex items-center justify-between gap-3" data-testid={`ref-tag-row-${t.slug}`}>
              {editing?.id === t.id ? (
                <div className="flex flex-wrap items-center gap-2 flex-1">
                  <input className="rounded-md border border-slate-300 px-3 py-1.5 text-sm" value={editing.nom} data-testid="ref-tag-edit-input" onChange={(e) => setEditing({ ...editing, nom: e.target.value })} />
                  <ColorPicker value={editing.couleur} onChange={(c) => setEditing({ ...editing, couleur: c })} testid="ref-tag-edit-color" />
                  <button onClick={save} data-testid="ref-tag-edit-save" className="p-1.5 text-cfhtb-success hover:bg-cfhtb-success/10 rounded-md"><Check className="h-4 w-4" /></button>
                  <button onClick={() => setEditing(null)} className="p-1.5 text-cfhtb-text hover:bg-slate-100 rounded-md"><X className="h-4 w-4" /></button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <span className="text-xs px-3 py-1 rounded-full font-medium text-white" style={{ backgroundColor: t.couleur }}>{t.nom}</span>
                    <span className="text-xs text-cfhtb-text/60">{t.nb_membres} membre(s)</span>
                  </div>
                  <div className="flex gap-1">
                    <button data-testid={`ref-tag-edit-${t.slug}`} onClick={() => setEditing({ id: t.id, nom: t.nom, couleur: t.couleur })} className="p-1.5 rounded-md text-cfhtb-text hover:bg-cfhtb-bg hover:text-cfhtb-primary"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`ref-tag-delete-${t.slug}`} onClick={() => remove(t)} className="p-1.5 rounded-md text-cfhtb-text hover:bg-cfhtb-error/10 hover:text-cfhtb-error"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </>
              )}
            </li>
          ))}
      </ul>
    </div>
  );
}

// Catégories documentaires (nom + parent)
function CategoriesCard() {
  const [cats, setCats] = useState(null);
  const [nom, setNom] = useState("");
  const [parentId, setParentId] = useState("");
  const [editing, setEditing] = useState(null);
  const load = () => api("/documents/categories").then(setCats).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!nom.trim()) return;
    try { await api("/admin/documents/categories", { method: "POST", body: { nom, parent_id: parentId || null } }); toast.success("Catégorie créée"); setNom(""); setParentId(""); load(); }
    catch (e) { toast.error(e.message); }
  };
  const save = async (c) => {
    try { await api(`/admin/documents/categories/${c.id}`, { method: "PUT", body: { nom: editing.nom, parent_id: editing.parent_id || null } }); toast.success("Catégorie mise à jour"); setEditing(null); load(); }
    catch (e) { toast.error(e.message); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Supprimer la catégorie « ${c.nom} » ? Les documents associés seront sans catégorie.`)) return;
    try { await api(`/admin/documents/categories/${c.id}`, { method: "DELETE" }); toast.success("Catégorie supprimée"); load(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm" data-testid="ref-categories-card">
      <div className="px-5 py-3 border-b border-slate-100 bg-cfhtb-bg/40">
        <h2 className="font-serif text-lg font-semibold text-cfhtb-heading">Catégories documentaires</h2>
        <p className="text-xs text-cfhtb-text mt-0.5">Classement de la base documentaire (hiérarchie possible).</p>
      </div>
      <div className="p-4 flex flex-wrap gap-2 border-b border-slate-100">
        <input data-testid="ref-cat-add-input" className={`${inputCls} flex-1 min-w-[160px]`} value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Ex. Livre blanc" />
        <select data-testid="ref-cat-parent-select" value={parentId} onChange={(e) => setParentId(e.target.value)} className="rounded-md border border-slate-300 px-3 py-2 text-sm">
          <option value="">Racine</option>
          {(cats || []).map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
        </select>
        <button data-testid="ref-cat-add-button" onClick={create} disabled={!nom.trim()} className="inline-flex items-center gap-1.5 bg-cfhtb-primary text-white rounded-md px-3 py-2 text-sm font-medium hover:bg-[#1f1975] disabled:opacity-50 shrink-0"><Plus className="h-4 w-4" /> Ajouter</button>
      </div>
      <ul className="divide-y divide-slate-50 max-h-80 overflow-y-auto" data-testid="ref-categories-list">
        {!cats ? <li className="px-5 py-6 text-sm text-cfhtb-text text-center">Chargement…</li>
          : cats.length === 0 ? <li className="px-5 py-6 text-sm text-cfhtb-text text-center">Aucune catégorie.</li>
          : cats.map((c) => (
            <li key={c.id} className="px-5 py-2.5 flex items-center justify-between gap-3" data-testid={`ref-cat-row-${c.slug}`}>
              {editing?.id === c.id ? (
                <div className="flex flex-wrap items-center gap-2 flex-1">
                  <input className="rounded-md border border-slate-300 px-3 py-1.5 text-sm" value={editing.nom} data-testid="ref-cat-edit-input" onChange={(e) => setEditing({ ...editing, nom: e.target.value })} />
                  <select className="rounded-md border border-slate-300 px-3 py-1.5 text-sm" value={editing.parent_id || ""} onChange={(e) => setEditing({ ...editing, parent_id: e.target.value })}>
                    <option value="">Racine</option>
                    {cats.filter((x) => x.id !== c.id).map((x) => <option key={x.id} value={x.id}>{x.nom}</option>)}
                  </select>
                  <button onClick={() => save(c)} data-testid="ref-cat-edit-save" className="p-1.5 text-cfhtb-success hover:bg-cfhtb-success/10 rounded-md"><Check className="h-4 w-4" /></button>
                  <button onClick={() => setEditing(null)} className="p-1.5 text-cfhtb-text hover:bg-slate-100 rounded-md"><X className="h-4 w-4" /></button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2" style={{ paddingLeft: c.parent_id ? "1.25rem" : 0 }}>
                    <span className="text-sm font-medium text-cfhtb-heading">{c.nom}</span>
                    <span className="text-xs text-cfhtb-text/60">{c.nb_documents} doc(s)</span>
                  </div>
                  <div className="flex gap-1">
                    <button data-testid={`ref-cat-edit-${c.slug}`} onClick={() => setEditing({ id: c.id, nom: c.nom, parent_id: c.parent_id || "" })} className="p-1.5 rounded-md text-cfhtb-text hover:bg-cfhtb-bg hover:text-cfhtb-primary"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`ref-cat-delete-${c.slug}`} onClick={() => remove(c)} className="p-1.5 rounded-md text-cfhtb-text hover:bg-cfhtb-error/10 hover:text-cfhtb-error"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </>
              )}
            </li>
          ))}
      </ul>
    </div>
  );
}

// Statuts / Types : libellé + couleur éditables, code système fixe
function LabelCard({ titre, description, endpoint, testPrefix }) {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api(endpoint).then(setItems).catch((e) => toast.error(e.message));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const save = async () => {
    try { await api(`${endpoint}/${editing.code}`, { method: "PUT", body: { label: editing.label, couleur: editing.couleur } }); toast.success("Enregistré"); setEditing(null); load(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm" data-testid={`${testPrefix}-card`}>
      <div className="px-5 py-3 border-b border-slate-100 bg-cfhtb-bg/40">
        <h2 className="font-serif text-lg font-semibold text-cfhtb-heading">{titre}</h2>
        <p className="text-xs text-cfhtb-text mt-0.5">{description}</p>
      </div>
      <ul className="divide-y divide-slate-50" data-testid={`${testPrefix}-list`}>
        {!items ? <li className="px-5 py-6 text-sm text-cfhtb-text text-center">Chargement…</li>
          : items.map((it) => (
            <li key={it.code} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`${testPrefix}-row-${it.code}`}>
              {editing?.code === it.code ? (
                <div className="flex flex-wrap items-center gap-2 flex-1">
                  <input className="rounded-md border border-slate-300 px-3 py-1.5 text-sm" value={editing.label} data-testid={`${testPrefix}-edit-input`} onChange={(e) => setEditing({ ...editing, label: e.target.value })} />
                  <ColorPicker value={editing.couleur} onChange={(c) => setEditing({ ...editing, couleur: c })} testid={`${testPrefix}-edit-color`} />
                  <button onClick={save} data-testid={`${testPrefix}-edit-save`} className="p-1.5 text-cfhtb-success hover:bg-cfhtb-success/10 rounded-md"><Check className="h-4 w-4" /></button>
                  <button onClick={() => setEditing(null)} className="p-1.5 text-cfhtb-text hover:bg-slate-100 rounded-md"><X className="h-4 w-4" /></button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <span className="text-xs px-3 py-1 rounded-full font-medium border" style={{ color: it.couleur, backgroundColor: `${it.couleur}1a`, borderColor: `${it.couleur}4d` }}>{it.label}</span>
                    <code className="text-xs text-cfhtb-text/50">{it.code}</code>
                    <span className="text-xs text-cfhtb-text/60">{it.nb_membres} membre(s)</span>
                  </div>
                  <button data-testid={`${testPrefix}-edit-${it.code}`} onClick={() => setEditing({ code: it.code, label: it.label, couleur: it.couleur })} className="p-1.5 rounded-md text-cfhtb-text hover:bg-cfhtb-bg hover:text-cfhtb-primary shrink-0"><Pencil className="h-4 w-4" /></button>
                </>
              )}
            </li>
          ))}
      </ul>
    </div>
  );
}

function TypesCard() {
  const [items, setItems] = useState(null);
  const [nom, setNom] = useState("");
  const [couleur, setCouleur] = useState(COULEURS[0]);
  const [editing, setEditing] = useState(null);
  const load = () => api("/admin/referentiels/types").then(setItems).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!nom.trim()) return;
    try { await api("/admin/referentiels/types", { method: "POST", body: { label: nom, couleur } }); toast.success("Type ajouté"); setNom(""); load(); }
    catch (e) { toast.error(e.message); }
  };
  const save = async () => {
    try { await api(`/admin/referentiels/types/${editing.code}`, { method: "PUT", body: { label: editing.label, couleur: editing.couleur } }); toast.success("Type mis à jour"); setEditing(null); load(); }
    catch (e) { toast.error(e.message); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Supprimer le type « ${t.label} » ?`)) return;
    try { await api(`/admin/referentiels/types/${t.code}`, { method: "DELETE" }); toast.success("Type supprimé"); load(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm" data-testid="ref-types-card">
      <div className="px-5 py-3 border-b border-slate-100 bg-cfhtb-bg/40">
        <h2 className="font-serif text-lg font-semibold text-cfhtb-heading">Types de membres</h2>
        <p className="text-xs text-cfhtb-text mt-0.5">Praticien et Visiteur sont des types système (non supprimables, ils pilotent l'annuaire). Vous pouvez en ajouter d'autres.</p>
      </div>
      <div className="p-4 space-y-2 border-b border-slate-100">
        <div className="flex gap-2">
          <input data-testid="ref-type-add-input" className={inputCls} value={nom} onChange={(e) => setNom(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), create())} placeholder="Ex. Étudiant, Partenaire…" />
          <button data-testid="ref-type-add-button" onClick={create} disabled={!nom.trim()}
            className="inline-flex items-center gap-1.5 bg-cfhtb-primary text-white rounded-md px-3 py-2 text-sm font-medium hover:bg-[#1f1975] disabled:opacity-50 shrink-0"><Plus className="h-4 w-4" /> Ajouter</button>
        </div>
        <ColorPicker value={couleur} onChange={setCouleur} testid="ref-type-color" />
      </div>
      <ul className="divide-y divide-slate-50" data-testid="ref-types-list">
        {!items ? <li className="px-5 py-6 text-sm text-cfhtb-text text-center">Chargement…</li>
          : items.map((t) => (
            <li key={t.code} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`ref-type-row-${t.code}`}>
              {editing?.code === t.code ? (
                <div className="flex flex-wrap items-center gap-2 flex-1">
                  <input className="rounded-md border border-slate-300 px-3 py-1.5 text-sm" value={editing.label} data-testid="ref-type-edit-input" onChange={(e) => setEditing({ ...editing, label: e.target.value })} />
                  <ColorPicker value={editing.couleur} onChange={(c) => setEditing({ ...editing, couleur: c })} testid="ref-type-edit-color" />
                  <button onClick={save} data-testid="ref-type-edit-save" className="p-1.5 text-cfhtb-success hover:bg-cfhtb-success/10 rounded-md"><Check className="h-4 w-4" /></button>
                  <button onClick={() => setEditing(null)} className="p-1.5 text-cfhtb-text hover:bg-slate-100 rounded-md"><X className="h-4 w-4" /></button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <span className="text-xs px-3 py-1 rounded-full font-medium border" style={{ color: t.couleur, backgroundColor: `${t.couleur}1a`, borderColor: `${t.couleur}4d` }}>{t.label}</span>
                    {t.systeme && <span className="text-[10px] uppercase tracking-wide text-cfhtb-text/50 border border-slate-200 rounded px-1.5 py-0.5">système</span>}
                    <span className="text-xs text-cfhtb-text/60">{t.nb_membres} membre(s)</span>
                  </div>
                  <div className="flex gap-1">
                    <button data-testid={`ref-type-edit-${t.code}`} onClick={() => setEditing({ code: t.code, label: t.label, couleur: t.couleur })} className="p-1.5 rounded-md text-cfhtb-text hover:bg-cfhtb-bg hover:text-cfhtb-primary"><Pencil className="h-4 w-4" /></button>
                    {!t.systeme && (
                      <button data-testid={`ref-type-delete-${t.code}`} onClick={() => remove(t)} className="p-1.5 rounded-md text-cfhtb-text hover:bg-cfhtb-error/10 hover:text-cfhtb-error"><Trash2 className="h-4 w-4" /></button>
                    )}
                  </div>
                </>
              )}
            </li>
          ))}
      </ul>
    </div>
  );
}

export default function ReferentielsAdmin() {
  const [instituts, setInstituts] = useState([]);
  const [professions, setProfessions] = useState([]);

  const load = () => {
    api("/admin/instituts").then(setInstituts).catch((e) => toast.error(e.message));
    api("/admin/professions").then(setProfessions).catch((e) => toast.error(e.message));
  };
  useEffect(() => { load(); }, []);

  const act = async (method, path, body, msg) => {
    try { await api(path, { method, ...(body ? { body } : {}) }); toast.success(msg); load(); }
    catch (e) { toast.error(e.message); }
  };
  const confirmDelete = (kind, label) => window.confirm(`Supprimer ${kind} « ${label} » ?`);

  return (
    <AppLayout variant="admin">
      <div data-testid="referentiels-page" className="space-y-6 animate-fade-in">
        <div className="flex items-center gap-3">
          <ListTree className="h-7 w-7 text-cfhtb-primary" />
          <div>
            <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">Référentiel</h1>
            <p className="text-sm text-cfhtb-text">Toutes les listes de valeurs et paramètres de l'extranet.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
          <CrudCard titre="Instituts de rattachement" description="Instituts membres de la CFHTB proposés dans les fiches membres."
            testPrefix="ref-instituts" items={(instituts || []).map((i) => ({ id: i.id, label: i.nom }))}
            onAdd={(nom) => act("POST", "/admin/instituts", { nom }, "Institut ajouté")}
            onRename={(id, nom) => act("PUT", `/admin/instituts/${id}`, { nom }, "Institut renommé")}
            onDelete={(it) => confirmDelete("l'institut", it.label) && act("DELETE", `/admin/instituts/${it.id}`, null, "Institut supprimé")} />

          <CrudCard titre="Professions" description="Professions de santé proposées dans les fiches membres."
            testPrefix="ref-professions" items={(professions || []).map((p) => ({ id: p.id, label: p.libelle }))}
            onAdd={(nom) => act("POST", "/admin/professions", { nom }, "Profession ajoutée")}
            onRename={(id, nom) => act("PUT", `/admin/professions/${id}`, { nom }, "Profession renommée")}
            onDelete={(it) => confirmDelete("la profession", it.label) && act("DELETE", `/admin/professions/${it.id}`, null, "Profession supprimée")} />

          <TagsCard />
          <CategoriesCard />

          <LabelCard titre="Statuts membres" description="Libellé et couleur d'affichage. Les codes système sont fixes (règles d'accès)."
            endpoint="/admin/referentiels/statuts" testPrefix="ref-statuts" />
          <TypesCard />
        </div>
      </div>
    </AppLayout>
  );
}
